// PersonaSelector.tsx
import { Box, Flex } from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ConversationType } from '../../generated/graphql';
import MultiSelectPersonaBar from './MultiSelectPersonaBar';
import MultiSelectPersonaFilter from './MultiSelectPersonaFilter';

interface PersonaType {
  id: string;
  name: string;
}

interface PersonaSelectorProps {
  allPersonas: PersonaType[];
  onSelectionChange?: (selectedPersonas: PersonaType[]) => void;
  type: ConversationType;
  initialSelected?: PersonaType;
}

const PersonaSelector: React.FC<PersonaSelectorProps> = ({
  allPersonas,
  onSelectionChange,
  type,
  initialSelected,
}) => {
  // IMPORTANT: Use a Set for state to track selected IDs
  const [selectedPersonaIds, setSelectedPersonaIds] = useState<Set<string>>(
    initialSelected ? new Set([initialSelected.id]) : new Set()
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(true);

  // Convert selected IDs to persona objects
  const selectedPersonas = useMemo(() => {
    return Array.from(selectedPersonaIds).map((id) => {
      const persona = allPersonas.find((p) => p.id === id);
      return persona || { id, name: 'Unknown' };
    });
  }, [selectedPersonaIds, allPersonas]);

  // This effect notifies the parent component when selections change
  useEffect(() => {
    if (onSelectionChange) {
      // Always pass a new array to trigger state updates in parent
      onSelectionChange([...selectedPersonas]);
    }
  }, [selectedPersonas, onSelectionChange]);

  // This function handles Set updates from MultiSelectPersonaFilter
  const handleSelectionChange = useCallback((newSelection: Set<string>) => {
    // We MUST create a new Set to ensure React detects the state change
    setSelectedPersonaIds(new Set(newSelection));
  }, []);

  // Handle removing a persona from the MultiSelectPersonaBar
  const handleRemovePersona = useCallback((id: string) => {
    setSelectedPersonaIds((prev) => {
      // Create a new Set, update it, and return
      const updated = new Set(Array.from(prev));
      updated.delete(id);
      return updated;
    });
  }, []);

  // Toggle dropdown visibility
  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen((prev) => !prev);
  }, []);

  const maxSelections = type === ConversationType.Persona ? 1 : 6;

  return (
    <Box width="100%">
      <Flex direction="column" gap={2} width="full">
        {/* Horizontal scrollable bar with selected personas */}
        <MultiSelectPersonaBar
          selectedPersonas={selectedPersonas}
          onRemovePersona={handleRemovePersona}
          onAddPersona={toggleDropdown}
          maxSelections={maxSelections}
          type={type}
        />

        {/* Dropdown for selecting personas - conditionally rendered */}
        {isDropdownOpen && (
          <Flex width="full" flexDirection="row" justifyContent="flex-end">
            <MultiSelectPersonaFilter
              selectedTwinIds={selectedPersonaIds}
              setSelectedTwins={handleSelectionChange}
              allTwins={allPersonas}
              maxSelections={maxSelections}
              onClose={() => setIsDropdownOpen(false)}
            />
          </Flex>
        )}
      </Flex>
    </Box>
  );
};

export default React.memo(PersonaSelector);
